import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageHeader } from 'components/PageHeader';
import { ROUTES } from 'constants/routes';
import { APPOINTMENT_TABLE_CLASS_NAME, useAppointmentAction } from 'hooks/useAppointmentAction';
import { type AppointmentWithDetails, type EnterpriseAppointmentFilter } from 'types/appointments';
import {
  getAppointmentFilterOptions,
  getSessionStatusColumnFilter,
  getSpecialityColumnFilter,
  getSpecialityLabel,
} from 'utils/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { TableActions } from './TableActions';
import { useFetchAppointments } from './hooks/useFetchAppointments';
import { useSessionsSummary } from './hooks/useSessionsSummary';

const getTableColumns = (): ColumnDef<AppointmentWithDetails>[] => [
  {
    id: 'dateTime',
    header: t('SessionsPage.Table.date.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'dateTime',
    enableSorting: true,
    cell: ({ row }): string => formatUnifiedForDisplay(row.original.dateTime),
  },
  {
    id: 'specialitiesBookable',
    header: t('SessionsPage.Table.speciality.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'specialitiesBookable',
    enableColumnFilter: true,
    meta: {
      columnFilter: getSpecialityColumnFilter(),
    },
    cell: ({ row }): string => getSpecialityLabel(row.original.specialitiesBookable),
  },
  {
    id: 'psychologist',
    header: t('AppointmentsTable.facultative.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row => `${row.psychologist?.name ?? ''} ${row.psychologist?.surname ?? ''}`.trim(),
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-31',
      },
    },
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'center',
    header: t('SessionsPage.Table.center.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-26',
      },
    },
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'patientName',
    header: t('SessionsPage.Table.patient.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'patientName',
    enableSorting: true,
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-38',
      },
    },
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'status',
    header: t('SessionsPage.Table.status.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'status',
    enableColumnFilter: true,
    meta: {
      columnFilter: getSessionStatusColumnFilter(),
    },
    cell: ({ row }): React.ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'actions',
    header: t('SessionsPage.Table.actions.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => <TableActions appointment={row.original} />,
  },
];

export const Appointments = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const { runAppointmentAction } = useAppointmentAction();
  const columns = getTableColumns();
  const { table, selectedFilter, setFilter } = useFetchAppointments(columns);
  const { stats, isLoading: isDescriptionLoading } = useSessionsSummary();

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t('InsuranceAppointmentsList.heading')}
        description={t('SessionsPage.summary', {
          total: stats.total,
          upcoming: stats.upcoming,
          previous: stats.previous,
          today: stats.today,
        })}
        isDescriptionLoading={isDescriptionLoading}
      >
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() =>
            navigate(ROUTES.APPOINTMENTS_RESERVE, { state: { flow: FLOW_MODES.search_patient } })
          }
        >
          {t('SessionsPage.scheduleCta')}
        </Button>
      </PageHeader>
      <PaginatedTable<AppointmentWithDetails, EnterpriseAppointmentFilter>
        autoPageSize
        table={table}
        columns={columns}
        emptyTitle={t('SessionsPage.empty')}
        tableClassName={APPOINTMENT_TABLE_CLASS_NAME}
        withSearch
        searchPlaceholder="SessionsPage.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        onRowAction={runAppointmentAction}
        selectedFilterKey={selectedFilter}
        setFilter={setFilter}
        filter={{
          accessorKey: 'status',
          allLabel: t('SessionsPage.filters.all'),
          options: getAppointmentFilterOptions(),
        }}
      />
    </div>
  );
};
